import { useState, useRef } from "react";
import ShowRoomButton from "../components/button/ShowRoomButton";
import LandingCard from "../components/cards/LandingCard";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import ContactForm from "../components/contact/ContactForm";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";

export default function LandingPage() {
	const ref = useRef(null);

	const handleClick = () => {
		ref.current.scrollIntoView({ behavior: "smooth" });
	};

	const isIpad = useMediaQuery({
		query: "(max-width: 1280px)",
	});

	const isMobile = useMediaQuery({
		query: "(max-width: 640px)",
	});

	const project = [
		{
			title: "Beach House",
			paragraph:
				"Dual shades are a practical option for controlling light, privacy, and style. Our client wanted privacy and a view during the day but needed total blackout and privacy at night because they lived at street level. These shades combine two fabrics in one system, offering both blackout and sheer options. This design lets you switch between the two, adjusting sunlight and privacy levels. Whether you want a soft glow, pitch darkness, or something in between, dual shades let you easily choose.",
		},
		{
			title: "Town House",
			paragraph:
				"Zebra shades are a stylish choice for windows, offering both practicality and good looks. Our client wanted to control how much sunlight came into their home, and they liked the interesting appearance of these shades from inside and outside. These shades have a special design with alternating sheer and solid panels, letting our client adjust the light level to their liking. We used them in living rooms, bedrooms, and offices. Plus, they give privacy without blocking the view completely, keeping rooms cozy while still feeling open.",
		},
		{
			title: "Luxury House",
			paragraph:
				"Roller shades are a flexible choice for window shades, valued for their simple, functional, and clean appearance. Our client wanted sunlight during the day but privacy around the clock. Translucent fabrics work well in this situation. These shades also have a clean, uncluttered style and fit easily with any decor, which is why they're popular in homes and businesses alike. You can find roller shades in lots of materials, colors, and levels of opacity to suit different tastes and requirements.",
		},
	];

	const cartData = [
		{
			picToRight: true,
			title: "How to Achieve the Luxury Look You Deserve",
			paragraphs: [
				"At Off Cut Shades, we're dedicated to ensuring your home improvement projects, particularly when it comes to window shades, reflect the quality and expertise you deserve. Many homeowners underestimate the risks associated with opting for cheaper alternatives when selecting window shades. However, choosing inferior shades not only compromises the aesthetic appeal of your home but could also diminish its overall value.",
				"Off Cut Shades stands as a beacon of quality and precision in the realm of window shades. We understand the importance of selecting the perfect shades that not only complement your interior design but also endure the test of time, enhancing your home's value. We guarantee the highest standard of service and after-sales support.",
				"When you choose Off Cut Shades, you're not just investing in window treatments; you're investing in the long-term beauty and value of your home.",
			],
			image: "main_2",
		},
		{
			picToRight: false,
			title: "Experience the Transformation of Quality Window Shades",
			paragraphs: [
				"Don't settle for frustrating and unreliable window shade providers. Many people don't realize the risk they are taking when they choose based solely on price. Choose a provider who understands the importance of quality window shades and how they enhance the overall value of your home. Opting for inferior window shades could actually devalue your home.",
				"At Off Cut Shades, we stand for quality and expert execution in selecting and installing the perfect window shades. We carefully choose shades that will stand the test of time and add value to your home.",
			],
			image: "main_4_1",
		},
		{
			picToRight: true,
			title: "Environmental Benifits of Quality Window Shades",
			paragraphs: [
				"At Off Cut Shades, we prioritize the health and safety of our customers and their environments. That's why our roller shade fabrics are Green Guard or OEKO-TEX certified, guaranteeing that they meet stringent standards for safe chemical emissions. These certifications ensure that our fabrics are free from harmful substances, making them ideal for use in residential and commercial spaces alike. With Off Cut Shades, you can have peace of mind knowing that your window treatments not only enhance your space aesthetically but also contribute to a healthier indoor environment for you and your loved ones",
			],
			image: "main_4_2",
		},
		{
			picToRight: false,
			title: "Enjoy the Finishing Touches that Are Suited To You",
			paragraphs: [
				"At Off Cut Shades, we understand that your home holds significance. It's a sanctuary for your family, a source of comfort, and a space that deserves to be treated with respect and care.",
				"We take the time to listen and understand your vision for your home, ensuring that every detail reflects your desires. With Off Cut Shades, you can trust that we'll provide the finishing touches you've always dreamed of, turning your house into the home you've always wanted.",
			],
			image: "main_4_3",
		},
	];

	const showRoomText = [
		"Explore the perfect window shade options for your home, bringing your Instagram and Pinterest inspirations to life. Our seasoned window shade and design team will expertly choose from our selections ensuring quality execution for the ideal look.",
		"Discover essential tips for selecting the best window shades for your house, regardless of your budget. We'll unveil straightforward strategies to ensure you secure a level of excellence within your budget.",
		"Receive a comprehensive project timeline and a fixed  price quote for your window shades. We'll transparently outline all costs upfront, providing clarity on every aspect of your investment.",
		"Furthermore, explore physical samples from our collection, featuring bespoke textures and finishes that can impart a distinct character to any space.",
	];

	const platinumGuarantee = [
		{
			title: "Guaranteed Fit",
			paragraph:
				"Our installers are experts in installing window shades and have decades of combined experience. In the unlikely event that a shade does not match your measurements, we will replace the shades at no cost.",
		},
		{
			title: "Manufacturing Guarantee",
			paragraph:
				"All of product have a full manufacturing warranty where we will replace or repair any shade with a manufacturing defect. Our warranty does not cover any physical damage or nonmanufacturing related issues.",
		},
		{
			title: "Satisfaction Guaranteed",
			paragraph:
				"If you are not completely satisfied with the quality, colour or design of your new shade, contact us within 14 days from the date of purchase. We will work with you to make it right.",
		},
	];

	const responsive = {
		superLargeDesktop: {
			// the naming can be any, depends on you.
			breakpoint: { max: 4000, min: 3000 },
			items: 5,
		},
		desktop: {
			breakpoint: { max: 3000, min: 1024 },
			items: 3,
		},
		tablet: {
			breakpoint: { max: 1024, min: 464 },
			items: 2,
		},
		mobile: {
			breakpoint: { max: 464, min: 0 },
			items: 1,
		},
	};

	return (
		<div>
			{/* first section */}
			<div
				className="w-full bg-cover bg-bottom h-auto max-sm:h-auto"
				style={{
					backgroundImage: isMobile
						? "url('/images/landing/main_1_mobile.jpg')"
						: "url('/images/landing/main_1.jpg')",
				}}
			>
				<div className="flex justify-between px-10 py-10 align-middle max-sm:justify-center">
					<div>
						<Link to={"/"}>
							<img src="/images/logoDark.png" width={180} />
						</Link>
					</div>
					<div className="flex justify-around">
						<div className="flex align-middle pt-2 max-sm:hidden">
							<div className="text-lg px-7 font-medium">
								Any Questions?
								<br />
								<span className="font-bold text-2xl max-2xl:text-xl">
									(416) 285-6001
								</span>
							</div>
							<div className="mt-2 max-2xl:w-8">
								<img src="/images/icons/ion_call.png" alt="phone logo" />
							</div>
						</div>
					</div>
				</div>
				<h1 className="text-5xl text-center font-extrabold max-2xl:text-4xl max-sm: px-4">
					Transform Your Home With Quality
					<br /> Window Shades.
				</h1>
				<h2 className="text-3xl text-center my-14 max-2xl:text-2xl max-sm: px-4">
					Book a Showroom Visit Today and Receive
					<br />
					<b>Free Measurement</b> Services in the GTA (Value $150).
				</h2>
				<div className="w-full h-1/2 flex justify-center items-end pb-32 max-sm:pb-10">
					<button
						className="bg-primary text-white px-12 py-3 font-bold text-lg hover:bg-secondary max-2xl:font-medium max-sm:px-5"
						onClick={handleClick}
					>
						Book a Showroom Visit and
						<br />
						Measurement Appointment Today
					</button>
				</div>
			</div>
			{/* <div className="flex justify-center mt-10 ml-5 ">
				<img src="/images/landing/main_logo.png" width={1200} />
			</div> */}
			<hr className="divide-black" />
			{/* second section */}
			{isIpad ? (
				<div className="h-auto my-24 w-full flex justify-center">
					<div className="w-2/3 max-sm:w-4/5">
						<h2 className="text-4xl font-extrabold text-center ">
							{cartData[0].title}
						</h2>
						<hr className="w-24 my-6 mx-auto" />
						<div className="w-full mb-7">
							<img
								src={`/images/landing/${cartData[0].image}.jpg`}
								width={500}
								height={400}
								className="w-full h-auto"
							/>
						</div>
						{cartData[0].paragraphs?.map((parparagraph, index) => (
							<div key={index} className="">
								<p className="leading-5 text-center">{parparagraph}</p>
								<br />
							</div>
						))}
						<div className="mt-2 text-center">
							<ShowRoomButton handleClick={handleClick} />
						</div>
					</div>
				</div>
			) : (
				<div className="h-auto flex justify-center my-24">
					<div className="w-1/3 h-auto px-10 ml-32 justify-end max-sm:ml-0 max-sm:w-4/5 ">
						<h2 className="text-4xl font-extrabold pr-48 max-2xl:pr-0 max-sm:pr-0">
							{cartData[0].title}
						</h2>
						<hr className="w-24 my-6" />
						{cartData[0].paragraphs?.map((parparagraph, index) => (
							<div key={index}>
								<p className="w-10/12 leading-5 max-2xl:w-11/12">
									{parparagraph}
								</p>
								<br />
							</div>
						))}
						<div className="mt-2">
							<ShowRoomButton handleClick={handleClick} />
						</div>
					</div>

					<img
						src={`/images/landing/${cartData[0].image}.jpg`}
						width={500}
						height={400}
					/>
				</div>
			)}

			{/* third section */}
			<div className="bg-[#F6F6F6] pt-24 w-full">
				<h2 className="text-4xl font-extrabold text-center">
					Experience the
					<br /> Transformation of Quality
					<br /> Window Shades
				</h2>
				<p className="w-1/3 text-center mx-auto my-10 px-7 leading-5 max-xl:w-2/3 max-sm:px-0">
					At Off Cut Shades, we refuse to settle for ordinary, and neither
					should you. Why settle when you can experience the breathtaking
					transformations that await you? We've curated a small sample of the
					incredible results our clients have achieved, showcasing the look and
					feel you've always dreamed of. Let these glimpses inspire you to
					unlock the full potential of your space with Off Cut Shades.
				</p>
				{project.map((item, index) => {
					return (
						<div key={index}>
							<hr className="w-24 my-8 mx-auto" />
							<h2 className="text-4xl font-extrabold text-center">
								{item.title}
								<br />
								Project
							</h2>
							<div className="flex justify-center my-10">
								<img
									src={`/images/landing/main_3_${index}_1.png`}
									className="mx-8"
									width={400}
									height={400}
									class="max-sm:hidden"
								/>
								<img
									src={`/images/landing/main_3_${index}_2.png`}
									width={400}
									height={400}
								/>
							</div>
							<p className="w-1/3 text-center mx-auto my-10 px-7 leading-5 max-xl:w-2/3 max-sm:px-0 max-sm:w-4/5">
								{item.paragraph}
							</p>
						</div>
					);
				})}
				<div className="mt-8 text-center">
					<ShowRoomButton handleClick={handleClick} />
				</div>
				<LandingCard
					picToRight={cartData[1].picToRight}
					title={cartData[1].title}
					paragraphs={cartData[1].paragraphs}
					image={cartData[1].image}
					handleClick={handleClick}
				/>
			</div>
			{/* fourth section */}
			<div>
				<LandingCard
					picToRight={cartData[2].picToRight}
					title={cartData[2].title}
					paragraphs={cartData[2].paragraphs}
					image={cartData[2].image}
					bgColor={"gray"}
					handleClick={handleClick}
				/>
				<LandingCard
					picToRight={cartData[3].picToRight}
					title={cartData[3].title}
					paragraphs={cartData[3].paragraphs}
					image={cartData[3].image}
					handleClick={handleClick}
				/>
			</div>
			{/* fifth section */}
			<div
				className=" my-28 w-full bg-cover w-full h-auto bg-center bg-no-repeat"
				style={{ backgroundImage: "url('/images/landing/main_5.jpg')" }}
			>
				<div className="w-full h-full flex justify-center items-center py-14">
					<div className="w-2/5 bg-white px-28 py-10 max-2xl:w-3/5 max-2xl:px-10 max-sm:w-11/12 ">
						<h2 className="text-4xl font-extrabold text-center">
							What you will get out of your showroom visit
						</h2>
						<p className="w-4/5 leading-5 mx-auto mt-14 max-sm:text-center">
							During your consultation with Off Cut Shades, you'll uncover
							valuable insights that can significantly enhance your property:
						</p>
						<br />
						<div className="w-4/5 mx-auto flex mt-2">
							<ul className="leading-5 w-auto list-outside list-disc max-sm:text-center">
								<li className="pb-5">
									{" "}
									Explore the perfect window shade options for your home,
									bringing your Instagram and Pinterest inspirations to life.
									Our seasoned window shade and design team will expertly choose
									from our selections ensuring quality execution for the ideal
									look.
								</li>
								<li className="pb-5">
									{" "}
									Discover essential tips for selecting the best window shades
									for your house, regardless of your budget. We'll unveil
									straightforward strategies to ensure you secure a level of
									excellence within your budget.
								</li>
								<li className="pb-5">
									{" "}
									Receive a comprehensive project timeline and a fixed price
									quote for your window shades. We'll transparently outline all
									costs upfront, providing clarity on every aspect of your
									investment.
								</li>
								<li className="pb-5">
									{" "}
									Furthermore, explore physical samples from our collection,
									featuring bespoke textures and finishes that can impart a
									distinct character to any space.
								</li>
							</ul>
						</div>

						<div className="mt-4 text-center">
							<ShowRoomButton handleClick={handleClick} />
						</div>
					</div>
				</div>
			</div>
			{/* sixth section */}
			<div className="my-20">
				<hr className="w-24 my-8 mx-auto" />
				<h2 className="text-4xl font-extrabold text-center mb-14 max-sm:px-4">
					Some of Our Window Shade <br /> Transformations
				</h2>
				<div className="flex justify-center w-full h-96">
					<Carousel
						swipeable={true}
						arrows={true}
						className="w-full"
						responsive={responsive}
						autoPlay={true}
						autoPlaySpeed={1000}
						infinite={true}
						centerMode={true}
						itemClass="carousel-item-padding-80-px"
					>
						<div className="px-1 ">
							<img src="images/landing/main_6_1.png" />
						</div>
						<div className="px-1">
							{" "}
							<img src="images/landing/main_6_2.png" />
						</div>
						<div className="px-1">
							{" "}
							<img src="images/landing/main_6_3.png" />
						</div>
						<div className="px-1">
							{" "}
							<img src="images/landing/main_6_4.png" />
						</div>
					</Carousel>
				</div>
				<div className="mt-24 text-center">
					<ShowRoomButton handleClick={handleClick} />
				</div>

				<div className="my-28 justify-center">
					<h2 className="text-4xl font-extrabold text-center max-sm: px-4 ">
						What customers are saying
						<br />
						about us
					</h2>
					<hr className="w-24 my-6 mx-auto" />
					<div className="w-full flex flex-col justify-center items-center">
						<img src="images/landing/main_7.png" width="100" height="100" />
						<h3 className="font-bold italic text-center py-2">-Terresa-</h3>
						<div>
							<Star />
						</div>
						<p className="leading-5 w-1/3  text-center max-2xl:w-1/2 max-sm:w-5/6">
							We love the blinds we purchased from Off Cut Shades several years
							ago for our cottage. They were easy to install and the quality and
							look enhanced the casual feel of our cottage. Recently, a bear
							tried to break in through the kitchen window. Luckily the bear
							failed but managed to damage the blinds in the process! The
							customer service at Off Cut Shades were so helpful in helping us
							to choose matching fabric and having the shade replaced!
						</p>
					</div>

					<br />
				</div>
			</div>
			{/* seventh section */}
			<div
				className="bg-cover h-auto flex flex-col items-center"
				style={{ backgroundImage: "url('/images/landing/main_8.jpg')" }}
			>
				<hr className="w-24 my-12 mx-auto" />
				<h2 className="text-4xl font-extrabold text-center mb-14 max-sm: px-4">
					The Off Cut Shades <br /> Guarentee
				</h2>
				{platinumGuarantee.map((item, index) => (
					<div
						className="w-2/5 bg-white flex items-center h-fit py-4 my-2 max-xl:w-3/5 max-sm:block max-sm:w-10/12"
						key={index}
					>
						<div className="text-5xl w-16 font-bold py-16 mx-10 max-sm:hidden">
							{index + 1}.
						</div>
						<div className="max-sm:text-center">
							<h3 className="text-xl font-bold">{item.title}</h3>
							<br />
							<p className="w-4/5 max-sm:w-full max-sm:px-4">
								{item.paragraph}
							</p>
						</div>
					</div>
				))}
				<div className="my-24 text-center">
					<ShowRoomButton handleClick={handleClick} />
				</div>
			</div>
			{/* eight section */}
			{/* <div className="w-full h-24">
				<img src="images/landing/black.png" />
			</div> */}
			{/* ninth section */}
			<div
				className="w-full bg-full h-auto bg-cover bg-bottom"
				style={{ backgroundImage: "url('/images/landing/main_9.jpg')" }}
			>
				<h2 className="text-4xl font-extrabold text-center py-20 max-2xl:text-3x max-sm: px-4">
					Claim Your Free, No Obligation <br /> “Showroom Visit”, Fixed-Price{" "}
					<br />
					Quote, and Project Timeline
				</h2>
				<hr className="w-24 mx-auto mb-6" ref={ref} />
				<h3 className="text-4xl text-center mb-16 max-2xl:text-3xl text-primary max-sm: px-4">
					BONUS: Receive Free Measurement
					<br />
					Services in the GTA ($150 Value)
				</h3>
				<div className="pb-96">
					<ContactForm landing={true} />
				</div>
			</div>
		</div>
	);
}
